<template>
  <div class="suite">
    <h2>Blockchain Forensics & On-chain Analysis</h2>
    <div class="mdc-layout-grid__inner">
      <div
        class="mdc-layout-grid__cell mdc-layout-grid__cell--span-5 suite__block --even"
        v-scrollreveal="{ delay: 200 }"
        ref="v2container"
      >
        <div class="chip-container">
          <div class="chip --blue"><SvgAnalyticsIcon /> Analytics</div>
        </div>
        <h3>Bubblemaps V2</h3>
        <p>
          Track real-time token distribution, uncover wallet clusters, and
          explore token history across chains - visually and instantly.
        </p>
        <Button raised target="_blank" link="https://v2.bubblemaps.io"
          >Get Started</Button
        >
        <Button outlined route="/v2">Read More</Button>
        <lottie-player
          :src="require(`@/assets/lottie/bubblemaps.json`)"
          background="transparent"
          speed="1"
          class="lottie-player --v2"
          loop
          :autoplay="autoplay"
          ref="v2player"
        ></lottie-player>
      </div>
      <div
        class="mdc-layout-grid__cell mdc-layout-grid__cell--span-5 suite__block"
        v-scrollreveal="{ delay: 200 }"
        ref="inteldeskcontainer"
      >
        <div class="chip-container">
          <div class="chip --green"><SvgInvestigationIcon /> InfoFi</div>
        </div>
        <h3>Intel Desk</h3>
        <p>
          Community-powered research driving the future of cryptocurrency
          analysis.
        </p>
        <Button raised target="_blank" link="https://intel.bubblemaps.io"
          >Get Started</Button
        >
        <Button outlined route="/intel-desk">Read More</Button>
        <lottie-player
          :src="require(`@/assets/lottie/inteldesk.json`)"
          background="transparent"
          speed="1"
          class="lottie-player --inteldesk"
          loop
          :autoplay="autoplay"
          ref="inteldeskplayer"
        ></lottie-player>
      </div>
      <div
        class="mdc-layout-grid__cell mdc-layout-grid__cell--span-5 suite__block --even"
        v-scrollreveal="{ delay: 200 }"
        ref="iframecontainer"
      >
        <div class="chip-container">
          <div class="chip --purple"><SvgCodeIcon /> Widget</div>
        </div>
        <h3>Iframe Integration</h3>
        <p>
          Integrate Bubblemaps into your dApp or analytics dashboard via iframe
          to bring on-chain data to life.
        </p>
        <Button raised target="_blank" link="https://docs.bubblemaps.io/iframe"
          >Get Started</Button
        >
        <lottie-player
          :src="require(`@/assets/lottie/iframe.json`)"
          background="transparent"
          speed="1"
          class="lottie-player --iframe"
          loop
          :autoplay="autoplay"
          ref="iframeplayer"
        ></lottie-player>
      </div>
      <div
        class="mdc-layout-grid__cell mdc-layout-grid__cell--span-5 suite__block"
        v-scrollreveal="{ delay: 200 }"
        ref="apicontainer"
      >
        <div class="chip-container">
          <div class="chip --yellow"><SvgDataIcon /> Infra</div>
        </div>
        <h3>API</h3>
        <p>
          Bring Bubblemaps analytics to your protocol, platform, or tool.
          Powering transparency, compliance, and data-driven insights for your
          users.
        </p>
        <Button
          raised
          target="_blank"
          link="https://pro.bubblemaps.io/api/plans"
          >Get Started</Button
        >
        <lottie-player
          :src="require(`@/assets/lottie/api.json`)"
          background="transparent"
          speed="1"
          class="lottie-player --api"
          loop
          :autoplay="autoplay"
          ref="apiplayer"
        ></lottie-player>
      </div>
    </div>
  </div>
</template>

<script>
import "@lottiefiles/lottie-player";
import Button from "@/components/atoms/material/Button.vue";

export default {
  name: "Suite",
  components: {
    Button,
  },
  mounted: function () {
    // Play/Pause animation on scroll
    // (for browsers supporting IntersectionObserver)
    let that = this;
    var v2observer = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        that.$refs.v2player.play();
      } else {
        that.$refs.v2player.pause();
      }
    });
    v2observer.observe(this.$refs.v2container);
    var inteldeskobserver = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        that.$refs.inteldeskplayer.play();
      } else {
        that.$refs.inteldeskplayer.pause();
      }
    });
    inteldeskobserver.observe(this.$refs.inteldeskcontainer);
    var iframeobserver = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        that.$refs.iframeplayer.play();
      } else {
        that.$refs.iframeplayer.pause();
      }
    });
    iframeobserver.observe(this.$refs.iframecontainer);
    var apiobserver = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        that.$refs.apiplayer.play();
      } else {
        that.$refs.apiplayer.pause();
      }
    });
    apiobserver.observe(this.$refs.apicontainer);
  },
  computed: {
    autoplay() {
      return (
        !("IntersectionObserver" in window) ||
        !("IntersectionObserverEntry" in window) ||
        !("intersectionRatio" in window.IntersectionObserverEntry.prototype) ||
        !("isIntersecting" in window.IntersectionObserverEntry.prototype)
      );
    },
  },
};
</script>

<style scoped lang="scss">
@import "src/assets/variables";

.suite {
  h2 {
    text-align: center;
  }
  .suite__block {
    @include theme-glass-effect;
    padding: 20px;

    .chip-container {
      display: flex;
    }

    .chip {
      background-color: #4a7de230;
      color: #4a7de2;
      fill: #4a7de2;
      padding: 5px 10px;
      border-radius: 5px;
      font-size: 0.8rem;
      font-weight: 600;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      gap: 5px;

      &.--blue {
        background-color: #4a7de230;
        color: #4a7de2;
        fill: #4a7de2;
      }

      &.--green {
        background-color: #4ae29630;
        color: #4ae296;
        fill: #4ae296;
      }

      &.--purple {
        background-color: #b15ae330;
        color: #b15ae3;
        fill: #b15ae3;
      }

      &.--yellow {
        background-color: #d8e24a30;
        color: #d8e24a;
        fill: #d8e24a;
      }

      svg {
        height: 14px;
        width: 14px;
        fill: inherit;
      }
    }

    h3 {
      margin-top: 0;
      margin-bottom: 10px;
      font-size: 1.7rem;
      font-weight: 600;
    }

    p {
      opacity: 0.7;
      margin-bottom: 5px;
    }

    button {
      margin-right: 10px;
      margin-top: 10px;
    }

    .lottie-player {
      height: 300px;

      &.--v2 {
        height: 270px;
        margin-top: 15px;
        margin-bottom: -5px;
      }

      &.--inteldesk {
        margin-top: -10px;
        margin-bottom: -20px;
      }
      &.--iframe {
        height: 270px;
        margin-top: 15px;
        margin-bottom: -5px;
      }
      &.--api {
        margin-top: -10px;
        margin-bottom: -30px;
      }
    }
  }
}

$mdc-grid-desktop-large: 1200px;

@media (min-width: $mdc-grid-tablet) and (max-width: calc($mdc-grid-desktop - 1px)) {
  .suite {
    .suite__block {
      grid-column-start: 2;
      grid-column-end: span 6;
    }
  }
}

@media (min-width: $mdc-grid-desktop) and (max-width: 899px) {
  .suite {
    .suite__block {
      grid-column-start: 2;
      grid-column-end: span 10;
    }
  }
}

@media (min-width: 900px) and (max-width: calc($mdc-grid-desktop-large - 1px)) {
  .suite {
    .suite__block {
      grid-column-end: span 6;
    }
  }
}

@media (min-width: $mdc-grid-desktop-large) {
  .suite {
    .suite__block {
      &.--even {
        grid-column-start: 2;
      }
    }
  }
}
</style>
